import { useEffect, useState, useRef } from 'react'
import './App.css'
import Layout from './components/layout/layout';
import { Outlet, useLocation } from 'react-router-dom';
import https from './service/httpsService';
import { AppToaster } from './components/notifications/AppToaster';
import type { PermissionsObject } from './components/layout/types/globalTypes';

const normalizePath = (rawUrl?: string | null) => {
    const trimmedUrl = rawUrl?.trim();

    if (!trimmedUrl || trimmedUrl.startsWith("#")) return "";

    try {
        const url = new URL(trimmedUrl, window.location.origin);
        return url.pathname.toLowerCase().replace(/\/+$/, "") || "/";
    } catch {
        return trimmedUrl.split("?")[0].toLowerCase().replace(/^\/?/, "/").replace(/\/+$/, "") || "/";
    }
};

const getRouteTitle = (pathname: string, permissions?: Record<string, PermissionsObject>) => {
    const currentPath = normalizePath(pathname);
    const matchingPermission = (Object.values(permissions ?? {}) as PermissionsObject[])
        .filter(permission => permission.displayName && normalizePath(permission.url))
        .sort((a, b) => normalizePath(b.url).length - normalizePath(a.url).length)
        .find(permission => {
            const permissionPath = normalizePath(permission.url);
            return currentPath === permissionPath || currentPath.startsWith(`${permissionPath}/`);
        });

    return matchingPermission?.displayName;
};

const App = () => {
    const dataFetch = useRef(false)
    const location = useLocation();

    const [count, setCount] = useState(null);
    const [data, setData] = useState<any>(null);

    useEffect(() => {
        const routeTitle = getRouteTitle(location.pathname, data?.permissions);

        if (routeTitle) {
            document.title = routeTitle;
        }
    }, [location.pathname, data?.permissions]);

    useEffect(() => {
        if (dataFetch.current) return;

        dataFetch.current = true

        https.get("api/v1/Account/Permissions").then((response) => {
            return response.data
        })
            .then((data) => {
                setData(data);
                window.localStorage.setItem('userData', JSON.stringify(data))
            }).catch((reason) => {
                console.log(reason)
            })
    }, []);

    useEffect(() => {
        let hasRecentActivity = false;
        let lastKeepAliveAt = Date.now();
        const keepAliveFrequencyMilliseconds = 5 * 60 * 1000;

        const sendKeepAlive = () => {
            lastKeepAliveAt = Date.now();
            hasRecentActivity = false;
            https.get('/api/v1/Account/keep-alive').catch(() => {
                // The shared response interceptor handles an expired token.
            });

            const webAppUrl = data?.webAppURL
                ?? window.localStorage.getItem('Referrer');
            if (webAppUrl) {
                fetch(`${webAppUrl.replace(/\/$/, '')}/Account/IncreaseServerTime`, {
                    method: 'GET',
                    credentials: 'include',
                    mode: 'no-cors',
                    cache: 'no-store'
                }).catch(() => {
                    // The API token flow remains authoritative if Web Core is unavailable.
                });
            }
        };

        const recordActivity = () => {
            hasRecentActivity = true;
            if (Date.now() - lastKeepAliveAt >= keepAliveFrequencyMilliseconds) {
                sendKeepAlive();
            }
        };
        const activityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];

        activityEvents.forEach(eventName =>
            window.addEventListener(eventName, recordActivity, { passive: true }));

        const keepAliveInterval = window.setInterval(() => {
            if (!hasRecentActivity) return;

            sendKeepAlive();
        }, keepAliveFrequencyMilliseconds);

        return () => {
            window.clearInterval(keepAliveInterval);
            activityEvents.forEach(eventName =>
                window.removeEventListener(eventName, recordActivity));
        };
    }, [data?.webAppURL]);

    return (
        <Layout data={data}>
            <Outlet />
            <AppToaster />
        </Layout>
    )
};

export default App
